'use client'

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type Ref,
} from 'react'
import Link from 'next/link'
import { askTutorQuestion, checkMyMessage } from '@/app/chat/actions'
import type { MessageCheck, TutorQaAnswer } from '@/lib/deepseek'
import { MessageCheckCard } from '@/components/message-check-card'
import { useVoiceInput } from '@/lib/use-voice-input'
import { spliceDictation, type Caret } from '@/lib/insert-dictation'
import { actionErrorMessage } from '@/lib/action-error'
import { QaAnswerCard } from '@/components/qa-answer-card'
import { VoiceHoldButtons } from '@/components/voice-hold-buttons'
import { detectLang, pickVoiceLang, type VoiceLang } from '@/lib/detect-lang'
import { QA_SUGGESTIONS } from '@/lib/ai-cache'
import { LimitNotice } from '@/components/limit-notice'
import { dictationHint, useDictationCaret } from '@/lib/use-dictation-caret'
import { useCoarsePointer } from '@/lib/use-coarse-pointer'

type QaItem = { kind: 'ask'; question: string; answer: TutorQaAnswer } | { kind: 'check'; question: string; check: MessageCheck }

// Режим поля: вопрос о языке или «Проверь моё сообщение» (очередь идей
// 2026-09-17, п. 3) — текст, который ученик собирается отправить турку.
type QaMode = 'ask' | 'check'

// Императивный хендл под tutor-fab.tsx: удержание кнопки-репетитора должно
// стартовать/останавливать запись напрямую, в обход кнопки микрофона внутри
// модалки (та работает по тому же принципу — пока держат).
export type TutorQaHandle = { startListening: () => void; stopListening: () => void }

// Поле ввода растёт под длину вопроса, но не бесконечно: дальше — свой скролл
// внутри поля, иначе длинный вопрос выдавил бы список ответов за край модалки
// (у неё max-h-[80vh], см. tutor-fab.tsx). ~6 строк.
const INPUT_MAX_H = 148

const FADE = 16
const QA_SCROLL_CSS = `
.tk-qa-scroll {
  justify-content: flex-start;
  padding: ${FADE}px 10px ${FADE}px 2px;
  margin-right: -10px;
  scroll-padding-top: ${FADE}px;
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 ${FADE}px, #000 calc(100% - ${FADE}px), transparent 100%);
  mask-image: linear-gradient(to bottom, transparent 0, #000 ${FADE}px, #000 calc(100% - ${FADE}px), transparent 100%);
}
.tk-qa-scroll > :first-child, .tk-qa-scroll > :last-child { margin-top: 0; margin-bottom: 0; }
.tk-qa-scroll::-webkit-scrollbar { width: 6px; }
.tk-qa-scroll::-webkit-scrollbar-track { background: transparent; }
.tk-qa-scroll::-webkit-scrollbar-thumb { background: oklch(0.78 0.14 25 / 0.45); border-radius: 6px; }
.tk-qa-scroll::-webkit-scrollbar-thumb:hover { background: oklch(0.78 0.14 25 / 0.7); }
.tk-qa-scroll::-webkit-scrollbar-button { display: none; height: 0; }
/* Стандартные scrollbar-width/color - только там, где нет ::-webkit-scrollbar
   (Firefox): в Chrome они отключают webkit-стили, и на Windows появляются
   системные стрелки ▲▼. */
@supports not selector(::-webkit-scrollbar) {
  .tk-qa-scroll { scrollbar-width: thin; scrollbar-color: oklch(0.78 0.14 25 / 0.45) transparent; }
}
`

// Единственный вызывающий — tutor-fab.tsx (глобальная кнопка-логотип,
// открывает TutorQa во всплывающем окне); inputRef нужен ему для автофокуса
// поля ввода при открытии, onLeave закрывает окно при переходе на /photo.
export const TutorQa = forwardRef<TutorQaHandle, { inputRef?: Ref<HTMLTextAreaElement>; onLeave?: () => void }>(function TutorQa(
  { inputRef, onLeave },
  ref,
) {
  const [items, setItems] = useState<QaItem[]>([])
  const [mode, setMode] = useState<QaMode>('ask')
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Сколько вопросов осталось на неделю (лимит FREE_WEEKLY_QA_LIMIT); null —
  // ещё не знаем (узнаём с первым ответом) или безлимит.
  const [qaLeft, setQaLeft] = useState<number | null>(null)
  // error — упор в недельный лимит (limited: true в askTutorQuestion).
  const [limited, setLimited] = useState(false)
  // 4 случайных частых вопроса для пустого окна — ответы на них уже в общем
  // кэше (lib/ai-cache.ts), приходят мгновенно. Компонент монтируется только
  // в портале открытой модалки, так что Math.random гидратации не мешает.
  const [suggestions] = useState(() => [...QA_SUGGESTIONS].sort(() => Math.random() - 0.5).slice(0, 4))
  // Язык последнего заданного вопроса — единственная зацепка для
  // автоопределения, когда поле пустое (голосом спрашивают именно так).
  const [lastLang, setLastLang] = useState<VoiceLang | null>(null)

  const voiceLang = pickVoiceLang(input, lastLang)

  const {
    isSupported: micSupported,
    isListening,
    interimText,
    error: micError,
    start: startVoice,
    stop: stopVoice,
  } = useVoiceInput(voiceLang, true)

  // Диктовка: новая запись заменяет текст, после нажатия в поле — вставляется
  // в место курсора (правила — lib/use-dictation-caret.ts). voiceBaseRef —
  // снимок поля на старте записи: пока держат кнопку, поле недоступно.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const { userCaret, setUserCaret, rememberCaret } = useDictationCaret(textareaRef, isListening)
  const voiceBaseRef = useRef<{ base: string; caret: Caret | null }>({ base: '', caret: null })
  // То же для рендера (ref при рендере читать нельзя): пока идёт запись,
  // в поле показывается снимок с вставленным «сырым» хвостом.
  const [voiceBase, setVoiceBase] = useState<{ base: string; caret: Caret | null }>({ base: '', caret: null })

  const applyVoiceText = useCallback((text: string) => {
    const { base, caret } = voiceBaseRef.current
    const r = spliceDictation(base, caret, text)
    setInput(r.text)
    if (caret) setUserCaret({ start: r.caret, end: r.caret })
  }, [setUserCaret])

  // Язык текущей записи — для подсветки нажатой кнопки и подписи «Слушаю…».
  const [recLang, setRecLang] = useState<VoiceLang | null>(null)

  // lang — явный выбор ученика (кнопки «Русский»/«Türkçe»); без него —
  // автоопределение (удержание самой кнопки-репетитора в tutor-fab.tsx).
  const beginVoice = useCallback(
    (lang?: VoiceLang) => {
      if (!micSupported || isListening || sending) return
      const chosen = lang ?? voiceLang
      setRecLang(chosen)
      setLastLang(chosen)
      // Место не выбрано — запись заменит текст; выбрано — вставится туда.
      const caret = input ? userCaret : null
      const snap = caret ? { base: input, caret } : { base: '', caret: null }
      voiceBaseRef.current = snap
      setVoiceBase(snap)
      startVoice(applyVoiceText, chosen)
    },
    [micSupported, isListening, sending, voiceLang, input, userCaret, startVoice, applyVoiceText],
  )

  const endVoice = useCallback(() => {
    if (isListening) stopVoice()
  }, [isListening, stopVoice])

  const activeRecLang = isListening ? recLang : null

  useImperativeHandle(ref, () => ({
    startListening: () => beginVoice(undefined),
    stopListening: () => endVoice(),
  }))

  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Пока ждём ответ — показать «…» внизу.
  useLayoutEffect(() => {
    if (sending) bottomRef.current?.scrollIntoView({ block: 'nearest' })
  }, [sending])

  // К НАЧАЛУ нового ответа (с вопросом), а не к его концу: ответ часто
  // длиннее окна, и scrollIntoView({block:'end'}) показывал бы хвост, начало
  // пришлось бы искать прокруткой вверх. scrollTo по контейнеру, а не
  // scrollIntoView: тот мог бы дёрнуть и страницу под модалкой. Прокрутка в
  // layout-эффекте по числу карточек, а не в requestAnimationFrame после
  // setItems, как было: на телефоне кадр приходил раньше коммита React,
  // последней карточкой оказывалась предыдущая, и список прокручивался к ней,
  // новый ответ приходилось искать (задача 10 из Telegram, 02.10.2026).
  useLayoutEffect(() => {
    if (items.length === 0) return
    const box = scrollRef.current
    const cards = box?.querySelectorAll<HTMLElement>('[data-qa-item]')
    const last = cards?.[cards.length - 1]
    if (box && last) box.scrollTo({ top: Math.max(0, last.offsetTop - FADE), behavior: 'smooth' })
  }, [items.length])

  // Курсор возвращается в поле после ответа только там, где мышь и физическая
  // клавиатура. На сенсорном экране фокус в поле ставит только сам ученик
  // нажатием, иначе всплывает экранная клавиатура и закрывает ответ.
  const coarse = useCoarsePointer()
  const refocusRef = useRef(false)
  useEffect(() => {
    if (sending || !refocusRef.current) return
    refocusRef.current = false
    if (!coarse) textareaRef.current?.focus()
  }, [sending, coarse])

  function setTextareaRef(el: HTMLTextAreaElement | null) {
    textareaRef.current = el
    if (typeof inputRef === 'function') inputRef(el)
    else if (inputRef) inputRef.current = el
  }

  // Во время записи в поле видно то, что уже распознано (плюс ещё не
  // финализированный хвост), а не набранный текст — расти оно должно
  // одинаково в обоих случаях.
  const shownText = isListening
    ? spliceDictation(voiceBase.base, voiceBase.caret, interimText).text
    : input

  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    // scrollHeight у textarea не включает рамку, а высота при box-sizing:
    // border-box — включает: без добавки на рамку поле каждый раз занижено
    // ровно на неё, и внутри появляется лишний скролл.
    el.style.height = 'auto'
    const borders = el.offsetHeight - el.clientHeight
    el.style.height = `${Math.min(el.scrollHeight + borders, INPUT_MAX_H)}px`
  }, [shownText])

  async function handleAsk(preset?: string) {
    const question = (preset ?? input).trim()
    if (!question || sending) return
    if (!preset) {
      setInput('')
      setUserCaret(null)
    }
    // Фокус с поля снимаем сами, а не через disabled на время ответа: Android
    // при снятии disabled возвращал фокус и поднимал экранную клавиатуру, а
    // пока она открыта, список ответов сжат до нескольких строк и новый ответ
    // приходилось искать (задача 10 из Telegram).
    textareaRef.current?.blur()
    refocusRef.current = true
    setSending(true)
    setError(null)
    setLastLang(detectLang(question) ?? lastLang)
    try {
      // Session-local контекст — последние 3 пары, без сохранения в БД (см.
      // Comments в .scratch/voice-ai-tutor/issues/02).
      if (mode === 'check') {
        const r = await checkMyMessage(question)
        setQaLeft(r.usage.limit === null ? null : Math.max(0, r.usage.limit - r.usage.used))
        if (!r.ok) {
          setError(r.error)
          setLimited(r.limited === true)
          if (!preset) setInput(question)
          return
        }
        setItems((prev) => [...prev, { kind: 'check', question, check: r.check }])
        return
      }
      const history = items
        .filter((it): it is Extract<QaItem, { kind: 'ask' }> => it.kind === 'ask')
        .slice(-3)
        .map((it) => ({ question: it.question, answer: it.answer.answer }))
      const result = await askTutorQuestion(question, history)
      setQaLeft(result.usage.limit === null ? null : Math.max(0, result.usage.limit - result.usage.used))
      if (!result.ok) {
        setError(result.error)
        setLimited(result.limited === true)
        if (!preset) setInput(question)
        return
      }
      const answer = result.answer
      setItems((prev) => [...prev, { kind: 'ask', question, answer }])
    } catch (e) {
      setError(actionErrorMessage(e, 'Не удалось получить ответ'))
      if (!preset) setInput(question)
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {/* Стили прокрутки — inline-тегом, не в globals.css: телефон может
          держать устаревший CSS-бандл. Тонкая золотистая полоса с отступом от
          карточек и мягкое затухание текста у верхнего/нижнего края вместо
          резкого обреза о шапку. */}
      <style dangerouslySetInnerHTML={{ __html: QA_SCROLL_CSS }} />
      <div ref={scrollRef} className="tk-scroll-area tk-qa-scroll" style={{ position: 'relative' }}>
        {items.length === 0 && !sending && (
          // Приветствие — белым и крупнее: золото на тёмно-синем читалось плохо.
          // Пример вопроса — на отдельной светлой плашке, чтобы выделялся.
          <div style={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, textAlign: 'center', padding: '0 8px' }}>
            <div style={{ fontSize: 17, lineHeight: 1.45, fontWeight: 600, color: 'oklch(0.98 0.005 240)', maxWidth: 520 }}>
              Спросите о турецком языке или о жизни в Турции - голосом или текстом, по-русски или по-турецки.
            </div>
            {/* Частые вопросы — нажатие сразу задаёт вопрос. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%', maxWidth: 520 }}>
              <span style={{ color: 'var(--tk-gold)', fontWeight: 700, fontSize: 16 }}>Частые вопросы</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
                {suggestions.map((q) => (
                  // Вопросы — целые фразы и переносятся: скругление как у карточки, не пилюли.
                  <button
                    key={q}
                    type="button"
                    onClick={() => handleAsk(q)}
                    className="tk-pill tk-pill--sm rounded-[14px] px-3.5 py-2 text-left text-[16px] leading-[1.35] whitespace-normal"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {items.map((item, i) => (
          <div key={i} data-qa-item="">
            {item.kind === 'ask' ? <QaAnswerCard question={item.question} answer={item.answer} /> : <MessageCheckCard original={item.question} check={item.check} />}
          </div>
        ))}

        {sending && <div className="tk-bubble tk-side-a opacity-60">...</div>}

        {error && limited && <LimitNotice text={error} compact />}
        {error && !limited && <div className="text-sm" style={{ color: 'oklch(0.75 0.1 25)' }}>{error}</div>}
        {!error && qaLeft !== null && qaLeft <= 5 && qaLeft > 0 && (
          <div className="text-xs" style={{ color: 'var(--tk-gold)' }}>На этой неделе осталось вопросов: {qaLeft}</div>
        )}
        {!error && qaLeft === 0 && <LimitNotice text="Вопросов на эту неделю больше нет. Новые появятся в понедельник." compact />}
        {micError && <div className="text-sm" style={{ color: 'oklch(0.75 0.1 25)' }}>{micError}</div>}
        <div ref={bottomRef} />
      </div>

      <div className="flex flex-shrink-0 flex-col gap-1.5">
        {/* «Фото» — ссылка на /photo, не режим: по просьбе владельца 04.10.2026; 05.10 короткие подписи
            в одну строку и красная переливающаяся заливка (.tk-pill-photo). */}
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="flex items-center gap-1.5" role="tablist" aria-label="Режим">
            <button type="button" role="tab" aria-selected={mode === 'ask'} className="tk-pill-toggle" data-on={mode === 'ask' ? '' : undefined} onClick={() => setMode('ask')}>
              Вопрос
            </button>
            <button type="button" role="tab" aria-selected={mode === 'check'} className="tk-pill-toggle" data-on={mode === 'check' ? '' : undefined} onClick={() => setMode('check')}>
              Проверить сообщение
            </button>
          </div>
          <Link href="/photo" className="tk-pill-toggle tk-pill-photo" aria-label="Перевод по фото" onClick={onLeave}>
            Фото
          </Link>
        </div>
        <div className="flex items-end gap-2">
          <textarea
            ref={setTextareaRef}
            rows={1}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            className="tk-text-answer tk-tutor-input"
            placeholder={isListening ? 'Говорите...' : mode === 'check' ? 'Текст для отправки по-турецки...' : 'О турецком или о жизни в Турции...'}
            value={shownText}
            disabled={sending || isListening}
            onChange={(e) => {
              setInput(e.target.value)
              rememberCaret(e.currentTarget)
            }}
            onSelect={(e) => rememberCaret(e.currentTarget)}
            onClick={(e) => rememberCaret(e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleAsk()
              }
            }}
          />
          <button
            type="button"
            className="tk-btn-primary flex h-[55px] shrink-0 items-center justify-center rounded-full px-6 text-[16px] font-bold"
            disabled={sending || isListening || !input.trim()}
            onClick={() => handleAsk()}
            aria-label="Отправить"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        {/* Голосовой вопрос: две кнопки «держите и говорите» (voice-hold-buttons.tsx). */}
        {micSupported && (
          <VoiceHoldButtons
            activeLang={activeRecLang}
            disabled={sending}
            onStart={beginVoice}
            onEnd={endVoice}
            idleHint={dictationHint(input, userCaret)}
            compact
          />
        )}
      </div>
    </>
  )
})
